import { Form } from "antd";
import cn from "classnames";
import { ExclamationCircleFilled } from "@ant-design/icons";

import { localeLabel } from "@/shared/utils/locale.utils";

import style from "./style.module.css";

export const LOCALE_SCOPED_FIELDS = ["name", "clientJobTitle"] as const;

function LocaleButton({
  locale,
  isActive,
  errorPaths,
  onSelect,
}: {
  locale: string;
  isActive: boolean;
  errorPaths: Array<(string | number)[]>;
  onSelect: (locale: string) => void;
}) {
  return (
    <Form.Item noStyle shouldUpdate>
      {(form) => {
        const hasError = errorPaths.some((path) => form.getFieldError(path).length > 0);

        return (
          <button
            type="button"
            aria-pressed={isActive}
            className={cn(style.editingLocaleButton, { [style.editingLocaleButtonActive]: isActive })}
            onClick={() => onSelect(locale)}
          >
            {localeLabel(locale)}
            {hasError && <ExclamationCircleFilled className={style.localeTabError} aria-label="has errors" />}
          </button>
        );
      }}
    </Form.Item>
  );
}

/**
 * Page-level language selector. Every localized field on the form renders the locale chosen here,
 * so an admin translates a page at a time rather than a field at a time.
 *
 * @param {string[]} locales - the assessment's enabled locales, in canonical order
 * @param {string} activeLocale - the locale currently being edited
 * @param {(locale: string) => void} onChange - called with the newly selected locale
 * @param {Array<(string | number)[]>} localizedPaths - form paths whose errors mark a locale as incomplete
 * @returns {JSX.Element | null} the selector, or null when only one language is enabled
 */
export default function EditingLocaleSwitcher({
  locales,
  activeLocale,
  onChange,
  localizedPaths,
}: {
  locales: string[];
  activeLocale: string;
  onChange: (locale: string) => void;
  localizedPaths: Array<(string | number)[]>;
}) {
  if (locales.length < 2) {
    return null;
  }

  return (
    <div className={style.editingLocale}>
      <label id="editing-locale-label">Currently Editing</label>
      <p className={style.helperText} id="editing-locale-desc">
        Select the language you are editing the content on
      </p>
      <div role="group" aria-labelledby="editing-locale-label" aria-describedby="editing-locale-desc">
        {locales.map((locale) => (
          <LocaleButton
            key={locale}
            locale={locale}
            isActive={locale === activeLocale}
            errorPaths={localizedPaths.map((path) => [...path, locale])}
            onSelect={onChange}
          />
        ))}
      </div>
    </div>
  );
}
